import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy } from '@/modules/Policy/interfaces/policy'

import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors, showNotification } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import AppArcSelector from '@/components/AppArcSelector.vue'
import { exportPolicyReports } from '@/modules/PolicyReports/gql/policy.gql'
import { policyStatus, policyStatusClass, billingType, statusValues, policyTypeKeys, policyTypeValues, policyType } from '@/modules/Policy/helpers/policy'
import ListPage from '@/mixins/list-page'
import SearchCarrier from '@/mixins/search-queries/search-carrier'
import SearchCarrierProduct from '@/mixins/search-queries/search-carrier-product'
import SearchProducer from '@/mixins/search-queries/search-producer'
import SearchGeneralAgent from '@/mixins/search-queries/search-ga'
import SearchBook from '@/mixins/search-queries/search-book'
import RecordTypesHandler from '@/mixins/record-types'
import dollarFilter from '@/utils/dollarFilter'
import ExportExcel from '@/mixins/export-excel'
import { dateToDisplay } from '@/utils/dateTime'
import { SnackType } from '@/interfaces/snack'
import { PageState } from '../../../store/modules/page'
import * as R from 'ramda'
import { User } from '@/modules/User/interfaces/user'
import { CheckboxModel } from '../interfaces/checkbox-model'
import FilterSetting from '../components/FilterSetting.vue'
import { exportCustomPolicyReports, getAllPolicyReportRules, getCustomPoliciesReport } from '../gql/report-rule.gql'
import { PolicyReportRule } from '../interfaces/report-rule'

export interface InfoItem {
  title: string
  value: string
}

@Component({
  components: {
    AppBreadcrumbs,
    AppRefresh,
    AppDialog,
    AppTableDate,
    AppArcSelector,
    FilterSetting
  }
})
export default class PolicyReports extends mixins(ListPage, RecordTypesHandler, SearchCarrier, SearchCarrierProduct, mixins(SearchProducer, SearchGeneralAgent, SearchBook, ExportExcel)) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Customize Reports'
    },
    {
      text: 'Reports'
    }
  ]
  searchVal = ''
  startDate = ''
  endDate = ''

  complexSortItems = []
  complexSortVals = ['EXPIRED_POLICY', 'EXPIRING_POLICY', 'COMMISSION_REPORT']
  complexSortParams = ['expired_policy', 'expiring_policy', 'commission_report']
  complexSortActive = -1

  policies: Policy[] = []
  policyReports: Policy[] = []
  tableLoading = false

  statisticsLoading = true
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }
  dollarFilter = dollarFilter

  totalProjectedPremium = '0.00'
  totalCommissionReceived = '0.00'
  totalProjectedCommission = '0.00'

  yesOrNo = ['Yes', 'No']

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    policyName: 'POLICY_NAME',
    effectiveDate: 'EFFECTIVE_DATE',
    expirationDate: 'EXPIRATION_DATE',
    number: 'POLICY_NUMBER',
    status: 'POLICY_STATUS'
  }

  tableColumn = [
    {
      field: 'number',
      title: 'Policy Number',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.policyNumber ? row.policyNumber : '-')
          ]
        }
      },
      sortable: true,
      resizable: true,
      width: 150
    },
    {
      field: 'policyName',
      title: 'Policy Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true,
      width: 200
    },
    {
      field: 'status',
      title: 'Status',
      slots: { default: 'status_default' },
      sortable: true,
      resizable: true,
      width: 144
    },
    {
      field: 'type',
      title: 'Type',
      slots: { default: 'type_default' },
      sortable: true,
      resizable: true,
      width: 130
    },
    {
      title: 'Carrier',
      field: 'carrier',
      slots: { default: 'carrier_name_default' },
      resizable: true,
      width: 120
    },
    {
      title: 'Insurance Product',
      field: 'carrierProduct',
      slots: { default: 'product_name_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Source',
      field: 'source',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.source ? row.source.name : '-')
          ]
        }
      },
      resizable: true,
      width: 100
    },
    {
      title: 'Book',
      field: 'book',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.book ? row.book.name : '-')
          ]
        }
      },
      resizable: true,
      width: 100
    },
    {
      title: 'GA',
      field: 'generalAgent',
      slots: {
        default: ({ row }: any, h: any) => {
          return [
            h('span', row.generalAgent ? row.generalAgent.name : '-')
          ]
        }
      },
      resizable: true,
      width: 100
    },
    {
      title: 'Effective Date',
      field: 'effectiveDate',
      slots: {
        default: 'effective_default'
      },
      resizable: true,
      sortable: true,
      width: 150
    },
    {
      title: 'Expiration Date',
      field: 'expirationDate',
      slots: {
        default: 'expiration_default'
      },
      sortable: true,
      resizable: true,
      width: 150
    },
    {
      title: 'Bind Date',
      field: 'bindDate',
      slots: {
        default: 'bind_default'
      },
      resizable: true,
      sortable: true,
      width: 120
    },
    {
      title: 'Net Premium',
      field: 'premium',
      slots: { default: 'premium_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Fees',
      field: 'stampingFee',
      slots: { default: 'fees_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Broker Fee',
      field: 'brokerFee',
      slots: { default: 'broker_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Billing Type',
      field: 'billingType',
      slots: { default: 'billing_type_default' },
      resizable: true,
      width: 120
    },
    {
      title: 'Premium Received',
      field: 'totalReceivedPremium',
      slots: { default: 'premium_received_default' },
      resizable: true,
      width: 150
    },
    {
      title: 'Commission',
      field: 'totalProjectedPremium',
      slots: { default: 'commission_projected_default' },
      resizable: true,
      width: 110
    },
    {
      title: 'Commission Received',
      field: 'totalReceivedCommission',
      slots: { default: 'commission_received_default' },
      resizable: true,
      width: 170
    },
    {
      title: 'Commission Completed',
      field: 'isCommissionPayOff',
      slots: { default: 'commission_pay_off_default' },
      resizable: true,
      width: 180
    },
    {
      title: 'Cancel Date',
      field: 'cancelDate',
      slots: { default: 'cancel_date_default' },
      resizable: true,
      width: 180
    }
  ]

  policyStatus = policyStatus
  policyStatusClass = policyStatusClass
  policyStatusOptions = statusValues
  policyTypeOptions = policyTypeValues
  billingType = billingType
  policyType = policyType

  dateToDisplay = dateToDisplay

  isMounted = false

  generateReportDialog = {
    show: false,
    title: 'Generate Report',
    text: ''
  }

  disableConfirm = false

  isActiveTableMenu = false
  tableCheckList = {
    left: 115,
    top: 177,
    remainingHeight: 196
  }
  tableCheckListShow = false
  selectColumns: any = []
  tableCheckListItems: CheckboxModel = {}

  rules: PolicyReportRule[] = []

  // 图标列固定宽度:50,其他列固定最小宽度150，窗口可视区域小于800时出现滚动条，防止顶部按钮被挤压,
  get columnWidth () {
    const { selectColumns, windowSize } = this
    const len = selectColumns.length
    const column = { width: 0 }
    if (len > 1) {
      const totalColumnWidth = (len - 1) * 150
      if (windowSize.w < 800) {
        column.width = Math.floor((800 - 132 - 50) / (len))
      } else {
        column.width = totalColumnWidth > windowSize.w - 132 ? 150 : Math.floor((windowSize.w - 132) / (len))
      }
    } else {
      // 只有图标列
      column.width = windowSize.w < 800 ? 800 - 132 : (windowSize.w - 132)
    }
    // 设置已选列宽
    const columns = selectColumns.map((item: any) => {
      return {
        ...item,
        width: len <= 1 ? column.width : (item.title ? column.width : 50)
      }
    })
    this.selectColumns = [...columns]
    return column.width
  }

  get tableMinWidth () {
    const { windowSize, columnWidth, selectColumns } = this
    if (windowSize.w < 800) {
      return (800 - 132) + 'px'
    }
    if (selectColumns.length > 1 && ((selectColumns.length - 1) * columnWidth > windowSize.w - 132)) {
      return columnWidth * (selectColumns.length - 1) + 'px'
    }
    return (windowSize.w - 132 + 'px')
  }

  get ruleNames () {
    return this.rules.map(rule => rule.name)
  }

  @State(state => state.page) page!: PageState
  @State(state => state.user.me) me!: User

  @Watch('me')
  onMeChanged (val: User) {
    if (val.id) {
      this.getAllRules()
      this.updateTableCheckListItems([])
    }
  }

  @Watch('tableCheckListItems')
  tableCheckListItemsChanged (val: CheckboxModel) {
    if (!val) {
      return
    }
    const { tableColumn } = this
    const arr = []
    for (let key in val) {
      if (val[key]) {
        const column = R.find((item) => item.title === key, tableColumn)
        arr.push(column)
      }
    }
    this.selectColumns = [...arr]
  }

  @Watch('tableCheckListShow')
  onTableCheckListChanged (val: boolean) {
    this.isActiveTableMenu = val
  }

  @Watch('filter')
  onFilterChanged (val: any) {
    this.tableCheckList.top = 246
    this.tableCheckList.remainingHeight = 260
  }

  async mounted () {
    this.onResize()
    window.onresize = () => {
      return (() => {
        this.$nextTick(() => {
          this.onResize()
        })
      })()
    }
    await this.restorePage(this.page[this.$route.name || ''])
    if (this.me) {
      this.updateTableCheckListItems([])
      this.getAllRules()
    }
  }

  async getAllRules () {
    if (!this.me) {
      return
    }
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllPolicyReportRules
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.rules = result.data.getAllPolicyReportRules as PolicyReportRule[]
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  onResize () {
    const container = this.$refs.reportscontainer as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  sumFees (item: Policy) {
    const { agencyFee, carrierPolicyFee, inspectionFee, stampingFee, taxesFee } = item
    const sum = +agencyFee + (+carrierPolicyFee) + (+inspectionFee) + (+stampingFee) + (+taxesFee)

    return dollarFilter(sum)
  }

  async getPolicies (reSort = true) {
    const { complexSortActive } = this
    const { rules } = this
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    if (!rules[complexSortActive]) {
      return
    }
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCustomPoliciesReport,
        variables: {
          ruleId: +rules[complexSortActive].id,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: ((currentPage - 1) * pageSize),
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policies = result.data.getCustomPoliciesReport.policies
      this.totalProjectedPremium = result.data.getCustomPoliciesReport.totalProjectedPremium
      this.totalCommissionReceived = result.data.getCustomPoliciesReport.totalCommissionReceived
      this.totalProjectedCommission = result.data.getCustomPoliciesReport.totalProjectedCommission
      this.isMounted = true
      this.tableLoading = false
      this.options.tablePage.total = result.data.getCustomPoliciesReport.paging.totalCount
      if (reSort) {
        this.updateTableCheckListItems(result.data.getCustomPoliciesReport.displayColumns)
      }

      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getPolicies()
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getPolicies(false)
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getPolicies()
  }

  routeToDetail (item: any) {
    return {
      name: 'PolicyDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToCarrierDetail (item: any) {
    return {
      name: 'CarrierDetailDetail',
      params: {
        id: item.carrier.id
      }
    }
  }

  routeToCarrierProductDetail (item: any) {
    return {
      name: 'CarrierProductDetailDetail',
      params: {
        id: item.carrierProduct.id
      }
    }
  }

  generateHandler () {
    const { complexSortActive, rules } = this
    this.generateReportDialog.text = rules[complexSortActive] ? rules[complexSortActive].name : ''
    this.generateReportDialog.show = true
  }

  async generateConfirm () {
    const { complexSortActive, rules } = this

    if (!rules[complexSortActive]) {
      return
    }

    try {
      const res = await this.$apollo.mutate({
        mutation: exportCustomPolicyReports,
        variables: {
          ruleId: +rules[complexSortActive].id
        }
      })
      if (res.data.exportCustomPolicyReports) {
        showNotification({
          msg: 'Generate report successfully',
          type: SnackType.Success
        })
      }
      this.generateReportDialog.show = false
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  showTableCheckList () {
    this.tableCheckListShow = true
  }

  updateTableCheckListItems (displayColumns: string[]) {
    const noneCol = !displayColumns.length
    const { tableColumn } = this
    const displayMap: CheckboxModel = displayColumns.reduce((prev: CheckboxModel, curr: string) => {
      prev[curr] = true
      return prev
    }, {})

    const tableCheckListItems: CheckboxModel = {}

    for (let col of tableColumn) {
      tableCheckListItems[col.title] = !!displayMap[col.title as string] || noneCol
    }

    this.tableCheckListItems = tableCheckListItems
  }
}
